import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { CartLine } from '../types/shop';
type CartContextValue = {
  lines: CartLine[];
  count: number;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  addItem: (productId: string, option?: string, qty?: number) => void;
  updateQty: (key: string, qty: number) => void;
  removeItem: (key: string) => void;
  clear: () => void;
};
const CartContext = createContext<CartContextValue | null>(null);
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lines, setLines] = useState<CartLine[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const addItem = useCallback((productId: string, option?: string, qty = 1) => {
    const key = option ? `${productId}::${option}` : productId;
    setLines(prev => {
      const existing = prev.find(l => l.key === key);
      if (existing) {
        return prev.map(l => l.key === key ? {
          ...l,
          qty: l.qty + qty
        } : l);
      }
      return [...prev, {
        key,
        productId,
        option,
        qty
      }];
    });
    setIsOpen(true);
  }, []);
  const updateQty = useCallback((key: string, qty: number) => {
    setLines(prev => qty <= 0 ? prev.filter(l => l.key !== key) : prev.map(l => l.key === key ? {
      ...l,
      qty
    } : l));
  }, []);
  const removeItem = useCallback((key: string) => {
    setLines(prev => prev.filter(l => l.key !== key));
  }, []);
  const clear = useCallback(() => setLines([]), []);
  const value = useMemo<CartContextValue>(() => ({
    lines,
    count: lines.reduce((sum, l) => sum + l.qty, 0),
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    addItem,
    updateQty,
    removeItem,
    clear
  }), [lines, isOpen, addItem, updateQty, removeItem, clear]);
  return <CartContext.Provider value={value} data-id="element-519" data-mp="brygppd1uzxv">{children}</CartContext.Provider>;
}
export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}